/**
 * ⚔️ Coliseum — War Hall · Phase 1
 *
 * Multi-party battle lobby. Two modes:
 *   • 3v3 PvP — three heroes vs three heroes, speed-based turn order
 *   • Party Dungeon — three players co-op through 3 rooms (boss at end)
 *
 * Phase 1 ships party formation + matchmaking queue. The battle engine
 * itself lands in Phase 2.
 *
 * NOTE: useEffect uses inline .then/.catch (not an async refresh()) to
 * sidestep the `react-hooks/set-state-in-effect` rule that breaks webpack.
 * Actions trigger a re-fetch by bumping a `tick` counter that the effect
 * depends on.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_LABEL = {
  warrior: "🗡️ Warrior",
  mage: "🔮 Mage",
  archer: "🏹 Archer",
  ash_rogue: "🗡️ Ash Rogue",
  cassandra_auditor: "⚖️ Cassandra & Ledger",
  blue_mage: "💙 Blue Mage",
};

// Per-match class roster (owner-locked Feb 2026) — Core 4 selectable +
// 6 channeled stars. Locked ones (🔒) work in Coliseum/Arcade as a
// sneak-peek demo channel. Story NPCs (Cari, Roc, etc.) stay out.
// Each card shows: portrait (when available) + name + tagline + 1-2
// signature skill names. Portrait URLs are owner-supplied (Feb 2026).
const PORTRAIT_BASE = "https://customer-assets.emergentagent.com/job_omni-brand-1/artifacts";
// 🎨 New owner-supplied class portraits (Feb 2026 v1) — host on a different CDN subdomain.
const PORTRAIT_BASE_V2 = "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts";
const ALL_MATCH_CLASSES = [
  {
    id: "warrior", icon: "⚔️", name: "The Warrior of Dragonious",
    tagline: "Frontline tank. Phys-damage workhorse.",
    skills: ["⚔️ Heavy Swing", "💢 Decimate (ult)"],
    portrait: `${PORTRAIT_BASE_V2}/f3dmwxjy_warrior-rpg-orange-v1.webp`,
    locked: false,
  },
  {
    id: "mage", icon: "🔮", name: "The Mage of Dragonious",
    tagline: "Phoenix-flame caster. Boss-deleting burst.",
    skills: ["💠✨ Arcane Siphon", "🌋 Volcanic Downpour"],
    portrait: `${PORTRAIT_BASE_V2}/kvhs3cyt_mage-rpg-purple-v1.webp`,
    locked: false,
  },
  {
    id: "archer", icon: "🏹", name: "The Archer of Dragonious",
    tagline: "Range + precision. Crit-leaning DPS.",
    skills: ["⚡ Quick Shot", "🎯 Bullseye"],
    portrait: `${PORTRAIT_BASE_V2}/y3ybhs82_archer-rpg-green-v1.webp`,
    locked: false,
  },
  {
    id: "ash_rogue", icon: "🌑", name: "The Ash Rogue of Dragonious",
    tagline: "Stealth + ash-fire DoT. Crit assassin.",
    skills: ["🌑 Shadow Cut", "🌫️ Smokescreen (ult)"],
    portrait: `${PORTRAIT_BASE_V2}/6htsr4yb_ashrogue-rpg-black-v1.webp`,
    locked: false,
  },
  // 🩸 Xyla — owner directive Feb 20, 2026: "Love to have Xyla first"
  {
    id: "flesh_harvester", icon: "🩸", name: "Xyla — The Flesh Harvester",
    tagline: "Your pain is my sustenance. Bleed, so that I may live.",
    skills: ["🩸 Silk Fan Sweep", "🌪️ Crimson Flutter"],
    portrait: "",
    accent: "#B91C1C",
    locked: false,
  },
  {
    id: "roc_gym_owner", icon: "🦅", name: "Roc — The Bodybuilder",
    tagline: "Flex your frame. Protect your gains.",
    skills: ["💪 Dumbbell Pumps", "🛡️ Perfect Alignment"],
    portrait: "",
    accent: "#D97706",
    locked: false,
  },
  {
    id: "fragile_prodigy", icon: "🛡️", name: "Bram — The Fragile Prodigy",
    tagline: "Just a sleepy guy. Don't worry about him.",
    skills: ["⚡ Just a Swing", "😴 Unremarkable Presence"],
    portrait: "",
    accent: "#64748B",
    locked: false,
  },
  {
    id: "david_lauer", icon: "👨‍🍳", name: "David Lauer — The Chef",
    tagline: "Today's Special. Cook · cleaver · party heal.",
    skills: ["🔪 Chef's Cut", "💚 Communal Revival"],
    portrait: `${PORTRAIT_BASE}/cqc3djcf_1780783454446.png`,
    locked: false,
  },
  {
    id: "blue_mage", icon: "💙", name: "Vesper — The Blue Mage",
    tagline: "Learn by eating. Cast by remembering.",
    skills: ["🔮 Kaleidoscope", "💙 Azure Renewal"],
    portrait: `${PORTRAIT_BASE}/6s83kngm_1780465471005.png`,
    locked: false,
  },
  {
    id: "cassandra_auditor", icon: "⚖️", name: "Cassandra & Ledger — The Auditor Duo",
    tagline: "The Auditor + the Falcon. Ledger never lies.",
    skills: ["📒 Ledger Strike", "⚖️ Balance the Books (ult)"],
    portrait: `${PORTRAIT_BASE}/w73fhg9v_1780683460962.png`,
    locked: false,
  },
  {
    id: "commoner_vanguard", icon: "🪣", name: "Nathaniel Bucket — Commoner Vanguard",
    tagline: "Commoner tank. No mana — runs on Scrap.",
    skills: ["🪙 Penny Shot", "🛡️ Vanguard Press (ult)"],
    portrait: `${PORTRAIT_BASE}/clagrbei_1780817098610.png`,
    locked: false,
  },
  {
    id: "blood_practitioner", icon: "🩸", name: "Valen / Valerie — The Blood Practitioner",
    tagline: "Two faces, one Sanguine school. HP is the resource.",
    skills: ["🩸 Lancet", "💚 Bleed Transfer"],
    portrait: `${PORTRAIT_BASE}/3k74acp0_1781208362972.png`,
    locked: false,
  },
  {
    id: "glitch_architect", icon: "👾", name: "Glitch — The Architect",
    tagline: "Architect of the Script. RAM is the resource.",
    skills: ["🔄 Restart", "💥 Stack Overflow (ult)"],
    portrait: `${PORTRAIT_BASE}/7ulh0qk1_1781028129071.png`,
    locked: false,
  },
  {
    id: "war_librarian", icon: "🐾", name: "Cari — The War Librarian",
    tagline: "Sweet pitbull. Tactical mastermind. She remembers… mostly.",
    skills: ["📖 Memory Delay", "🌀 Archive Gravity Well (ult)"],
    portrait: "",
    accent: "#A16207",
    locked: false,
  },
  {
    id: "shadow_director", icon: "🐈‍⬛", name: "Taylor-Nightingale — The Shadow Director",
    tagline: "Director of nothing. Predator of everything.",
    skills: ["🥱 Annoyed Yawn", "🦉 Under Protest (ult)"],
    portrait: "",
    accent: "#4B0082",
    locked: false,
  },
  {
    id: "lawman_brewer", icon: "👮‍♂️", name: "Chief Wesley Blanks — The Lawman",
    tagline: "Hyper-vigilant cop. Everything looks suspicious. Trust him.",
    skills: ["🔍 Inspection", "⚖️ Last Call (ult)"],
    portrait: "",
    accent: "#1E3A8A",
    locked: false,
  },
  {
    id: "scrap_alchemist", icon: "🦉", name: "Miko — The Scrap Alchemist",
    tagline: "Hoard, then dump. The bag is the spell.",
    skills: ["🔧 Tinkering", "🌈 Master's Exhibition (ult)"],
    portrait: "",
    accent: "#059669",
    locked: false,
  },
  {
    id: "afflicted_vanguard", icon: "🦏", name: "Corros — The Blighted Shell",
    tagline: "I don't endure your poisons. I drink them.",
    skills: ["🗡️ Corrupted Cut", "💥 Grand Shell-Break (ult)"],
    portrait: "",
    accent: "#9F1239",
    locked: false,
  },
  {
    id: "weaver_of_fates", icon: "🕸️", name: "Kaelen — Weaver of Fates",
    tagline: "Your threads were spun long before you arrived.",
    skills: ["🩸 Guillotine Slice", "🌟 Golden Conduit"],
    portrait: "",
    accent: "#7C3AED",
    locked: false,
  },
  // 🚫 Not in this list: celestial_vanguard (Astraea — dies in her arc) +
  //    mother_phoenix (in Flux or dead depending on timeline).
  //    See canon: /app/memory/ARENA_HERO_ELIGIBILITY_CANON_FEB2026.md
];

const MODE_THEME = {
  pvp_3v3: {
    border: "border-red-700/60",
    bg: "from-red-950/40 via-black/60 to-rose-950/30",
    badge: "bg-red-900/50 text-red-200 border-red-700/60",
    accent: "text-red-200",
  },
  dungeon_3: {
    border: "border-emerald-700/60",
    bg: "from-emerald-950/40 via-black/60 to-teal-950/30",
    badge: "bg-emerald-900/50 text-emerald-200 border-emerald-700/60",
    accent: "text-emerald-200",
  },
};

function elapsed(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}m ${rs}s`;
}

async function postAction(path, body) {
  try {
    const r = await axios.post(`${API}${path}`, body || {});
    return { ok: true, data: r.data };
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Action failed.";
    toast.error(msg);
    return { ok: false, error: msg };
  }
}

const STATUS_CHIP = {
  available: { label: "Available", cls: "bg-emerald-900/40 text-emerald-200 border-emerald-700/50" },
  invited:   { label: "Invited",   cls: "bg-amber-900/40 text-amber-200 border-amber-700/50" },
  in_party:  { label: "In Party",  cls: "bg-indigo-900/40 text-indigo-200 border-indigo-700/50" },
  ash:       { label: "Ash Form",  cls: "bg-red-900/40 text-red-200 border-red-700/50" },
};

function CandidateList({ list, emptyMsg, onInvite, busy }) {
  if (!list) {
    return <div className="text-xs text-neutral-500 italic py-2">Loading…</div>;
  }
  if (list.length === 0) {
    return <div className="text-xs text-neutral-500 italic py-2" data-testid="picker-empty">{emptyMsg}</div>;
  }
  return (
    <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="picker-list">
      {list.map((c) => {
        const chip = STATUS_CHIP[c.status] || STATUS_CHIP.available;
        const canInvite = c.status === "available";
        return (
          <div
            key={c.user_id}
            data-testid={`picker-row-${c.user_id}`}
            className="flex items-center gap-2 px-2 py-1.5 rounded bg-neutral-900/70 border border-neutral-800"
          >
            <div className="text-xl">{c.pet_icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-neutral-100 truncate">{c.name}</div>
              <div className="text-[10px] text-neutral-500 truncate">
                {c.handle ? `@${c.handle}` : "—"} · ⚡ {c.power_level}
              </div>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[9px] border ${chip.cls} uppercase tracking-wider font-bold`}>
              {chip.label}
            </span>
            {canInvite && (
              <button
                onClick={() => onInvite(c.user_id, c.name)}
                disabled={busy}
                data-testid={`picker-invite-${c.user_id}`}
                className="px-3 py-1 rounded bg-amber-700 hover:bg-amber-600 disabled:opacity-50 text-white text-[10px] font-bold uppercase tracking-wider"
              >
                Invite
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TrophyCase({ history }) {
  if (!history) return null;
  const { wins, losses, total, winrate_pct, recent } = history;
  const hasFights = total > 0;
  return (
    <div
      data-testid="trophy-case"
      className="rounded-2xl border-2 border-amber-700/50 bg-gradient-to-br from-amber-950/30 via-black/70 to-black/80 p-5 mb-6 relative overflow-hidden"
    >
      <div className="absolute -top-10 -right-10 text-9xl opacity-[0.06] select-none pointer-events-none">🏆</div>
      <div className="relative flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70 font-bold">Trophy Case</div>
          <h2 className="text-2xl font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>
            BATTLE RECORD
          </h2>
        </div>
        {hasFights ? (
          <div className="flex items-center gap-3" data-testid="trophy-wl-badge">
            <div className="text-center">
              <div className="text-[9px] uppercase tracking-widest text-emerald-300/70">Wins</div>
              <div className="text-2xl font-black text-emerald-300" data-testid="trophy-wins">{wins}</div>
            </div>
            <div className="text-neutral-700 text-3xl font-thin">·</div>
            <div className="text-center">
              <div className="text-[9px] uppercase tracking-widest text-red-300/70">Losses</div>
              <div className="text-2xl font-black text-red-300" data-testid="trophy-losses">{losses}</div>
            </div>
            <div className="text-neutral-700 text-3xl font-thin">·</div>
            <div className="text-center">
              <div className="text-[9px] uppercase tracking-widest text-amber-300/70">Winrate</div>
              <div className="text-2xl font-black text-amber-200" data-testid="trophy-winrate">{winrate_pct}%</div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-amber-300/70 italic" data-testid="trophy-empty">
            No fights yet — your first battle awaits below.
          </div>
        )}
      </div>

      {hasFights && (
        <div
          className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1"
          data-testid="trophy-scroll"
          style={{ scrollbarWidth: "thin" }}
        >
          {recent.map((m) => {
            const tone = m.won
              ? "border-emerald-600/70 from-emerald-950/40 to-black/70"
              : m.winner === "mobs"
                ? "border-red-700/60 from-red-950/40 to-black/70"
                : "border-neutral-700 from-zinc-950/40 to-black/70";
            const badge = m.won ? "🏆 WIN" : m.winner === "mobs" ? "💀 WIPED" : "✖ LOSS";
            const badgeColor = m.won
              ? "text-emerald-300"
              : m.winner === "mobs" ? "text-red-300" : "text-neutral-400";
            return (
              <div
                key={m.match_id}
                data-testid={`trophy-card-${m.match_id}`}
                className={`flex-shrink-0 w-48 rounded-xl border-2 ${tone} bg-gradient-to-br p-3`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-2xl">{m.scenario_icon}</span>
                  <span className={`text-[10px] font-black ${badgeColor}`}>{badge}</span>
                </div>
                <div className="text-[9px] uppercase tracking-widest text-amber-300/60">{m.scenario}</div>
                <div className="text-xs font-bold text-neutral-100 truncate" title={m.opponent_label}>
                  vs {m.opponent_label}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1 text-[14px]">
                  {(m.teammates || []).slice(0, 3).map((t, idx) => (
                    <span
                      key={idx}
                      title={`${t.name}${t.is_ai ? " (AI)" : ""}${t.survived ? "" : " — fell"}`}
                      className={`${t.survived ? "" : "opacity-30 grayscale"} ${t.is_ai ? "ring-1 ring-cyan-500/40 rounded-full" : ""}`}
                    >
                      {t.pet_icon}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2 text-[10px] text-neutral-400">
                  <span>{m.rounds} rounds</span>
                  {m.chits > 0 && (
                    <span className="text-amber-300" data-testid={`trophy-chits-${m.match_id}`}>🪙 +{m.chits}</span>
                  )}
                </div>
                {m.underdog && (
                  <div
                    data-testid={`trophy-underdog-${m.match_id}`}
                    title={`Their loadout was ${m.gear_diff} power higher — you still won.`}
                    className="mt-1.5 inline-block px-2 py-0.5 rounded-full text-[9px] font-black bg-amber-900/40 text-amber-200 border border-amber-600/60"
                  >
                    🦦 UNDERDOG · ⚡+{m.gear_diff}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ColiseumPage() {
  const { isAuthed } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inviteHandle, setInviteHandle] = useState("");
  const [pickedMode, setPickedMode] = useState("pvp_3v3");
  const [tick, setTick] = useState(0);
  const [clock, setClock] = useState(0);
  const [candidates, setCandidates] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerTab, setPickerTab] = useState("guildmates");
  const [history, setHistory] = useState(null);
  const [activeMatchId, setActiveMatchId] = useState(null);
  // 🔁 Roc #52 — tap-to-swap formation. First tap = select a hero; second
  // tap on another hero = swap their col+row positions.
  const [selectedUserId, setSelectedUserId] = useState(null);
  const autoRedirectedRef = useRef(false);

  // Fetch + auto-refresh every 5s, plus on-demand via `tick` bump.
  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    const fetchOnce = () => {
      axios
        .get(`${API}/coliseum/state`)
        .then((r) => { if (!cancelled) { setState(r.data); setError(null); } })
        .catch((e) => {
          if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Failed to load Coliseum.");
        });
      axios
        .get(`${API}/coliseum/party/candidates`)
        .then((r) => { if (!cancelled) setCandidates(r.data); })
        .catch(() => { /* candidates are optional — silent fail */ });
      // 🚪 QA Bug #9 (2026-10) — NO auto-redirect into a match.
      // Previous behaviour redirected the user straight into the match on
      // every lobby mount, trapping them in a bot match with no obvious
      // escape. The active-match banner below already surfaces a Resume
      // button + Forfeit shortcut, so the user is always in control.
      axios
        .get(`${API}/coliseum/match/active`)
        .then((r) => {
          if (cancelled) return;
          const mid = r.data?.match_id || null;
          setActiveMatchId(mid);
          // No navigate() call — user chooses Resume or Forfeit from the banner.
        })
        .catch(() => { /* silent — not in a match */ });
      // Trophy case — last 5 finished matches + W/L
      axios
        .get(`${API}/coliseum/history?limit=5`)
        .then((r) => { if (!cancelled) setHistory(r.data); })
        .catch(() => { /* history is optional — silent fail */ });
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAuthed, tick, navigate]);

  // Local clock for the "Queued for Xs" counter — separate so it never
  // collides with fetch state.
  useEffect(() => {
    const id = setInterval(() => setClock((c) => c + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const bump = () => setTick((t) => t + 1);

  const onCreateParty = async () => {
    setBusy(true);
    const r = await postAction("/coliseum/party/create", { mode: pickedMode });
    setBusy(false);
    if (r.ok) { toast.success("Party formed — invite some heroes!"); bump(); }
  };

  const onSwitchMode = async (mode) => {
    if (state?.party && state.party.mode === mode) return;
    if (state?.party) {
      setBusy(true);
      const r = await postAction("/coliseum/party/mode", { mode });
      setBusy(false);
      if (r.ok) bump();
    } else {
      setPickedMode(mode);
    }
  };

  const onInvite = async () => {
    const h = inviteHandle.trim().replace(/^@/, "");
    if (!h) return;
    setBusy(true);
    const r = await postAction("/coliseum/party/invite", { handle: h });
    setBusy(false);
    if (r.ok) { setInviteHandle(""); toast.success(`Invite sent to @${h}`); bump(); }
  };

  const onInviteById = async (target_user_id, name) => {
    setBusy(true);
    const r = await postAction("/coliseum/party/invite-by-id", { target_user_id });
    setBusy(false);
    if (r.ok) { toast.success(`Invite sent to ${name}`); bump(); }
  };

  const onCancelInvite = async (target_user_id) => {
    setBusy(true);
    const r = await postAction("/coliseum/party/cancel-invite", { target_user_id });
    setBusy(false);
    if (r.ok) bump();
  };

  const onLeave = async () => {
    if (!window.confirm("Leave the party?")) return;
    setBusy(true);
    const r = await postAction("/coliseum/party/leave");
    setBusy(false);
    if (r.ok) { toast.success("Left the party."); bump(); }
  };

  const onKick = async (target_user_id, name) => {
    if (!window.confirm(`Kick ${name}?`)) return;
    setBusy(true);
    const r = await postAction("/coliseum/party/kick", { target_user_id });
    setBusy(false);
    if (r.ok) { toast.success(`Kicked ${name}.`); bump(); }
  };

  // 🔀 Leader-only — shuffle a member between Front and Back row.
  const onMoveRow = async (target_user_id, row) => {
    setBusy(true);
    const r = await postAction("/coliseum/party/formation", { target_user_id, row });
    setBusy(false);
    if (r.ok) { toast.success(`Moved to ${row} row`); bump(); }
  };

  // 🔁 Leader-only — tap-to-swap. First tap selects, second tap swaps.
  const onTapHero = async (user_id) => {
    if (!selectedUserId) {
      setSelectedUserId(user_id);
      return;
    }
    if (selectedUserId === user_id) {
      // Tapped the same hero again — deselect.
      setSelectedUserId(null);
      return;
    }
    setBusy(true);
    const r = await postAction("/coliseum/party/swap", {
      user_id_a: selectedUserId, user_id_b: user_id,
    });
    setBusy(false);
    setSelectedUserId(null);
    if (r.ok) { toast.success("🔁 Swapped positions"); bump(); }
  };

  const onAccept = async (party_id) => {
    setBusy(true);
    const r = await postAction("/coliseum/party/accept", { party_id });
    setBusy(false);
    if (r.ok) { toast.success("Joined the party!"); bump(); }
  };

  const onDecline = async (party_id) => {
    setBusy(true);
    const r = await postAction("/coliseum/party/decline", { party_id });
    setBusy(false);
    if (r.ok) bump();
  };

  const onQueueEnter = async () => {
    setBusy(true);
    const r = await postAction("/coliseum/queue/enter");
    setBusy(false);
    if (r.ok) { toast.success("Queued — searching for opponents..."); bump(); }
  };

  const onQueueLeave = async () => {
    setBusy(true);
    const r = await postAction("/coliseum/queue/leave");
    setBusy(false);
    if (r.ok) bump();
  };

  const onPickMatchClass = async (class_id) => {
    setBusy(true);
    // Backend endpoint reuses `mode` field for class_id payload
    const r = await postAction("/coliseum/party/match-class", { mode: class_id });
    setBusy(false);
    if (r.ok) { toast.success(`Match class set to ${class_id.replace("_", " ")}`); bump(); }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
            ⚔️ The Coliseum is closed.
          </h1>
          <p className="text-neutral-400 mt-3">Sign in to form a party and challenge other guilds.</p>
          <Link to="/" className="inline-block mt-6 px-6 py-2 rounded bg-red-700 hover:bg-red-600 font-bold">Go Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const party = state?.party;
  const me = state?.me;
  const invites = state?.pending_invites || [];
  const isLeader = party && me && party.leader_user_id === me.user_id;
  const partyMode = party?.mode || pickedMode;
  const theme = MODE_THEME[partyMode] || MODE_THEME.pvp_3v3;
  const teamPower = party ? party.members.reduce((s, m) => s + (m.power_level || 0), 0) : 0;
  const isQueued = !!party?.queued_at;
  const maxParty = state?.max_party_size || 3;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-black to-black text-white">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="coliseum-page">
        {/* Hero banner */}
        <div className={`rounded-3xl border-2 ${theme.border} bg-gradient-to-br ${theme.bg} p-6 mb-6 relative overflow-hidden`}>
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)",
            backgroundSize: "60px 60px, 90px 90px",
          }} />
          <div className="relative flex items-start gap-5">
            <div className="text-6xl flex-shrink-0 drop-shadow-[0_0_20px_rgba(255,80,80,0.3)]">⚔️</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70 font-bold">War Hall</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-amber-100 leading-none mt-1"
                  style={{ fontFamily: "Anton, sans-serif", letterSpacing: "0.02em" }}>
                THE COLISEUM
              </h1>
              <p className="text-sm text-amber-100/80 mt-3 max-w-2xl">
                Form a party of three. Queue against another guild&rsquo;s heroes, or
                crawl a dungeon together. <span className="text-red-300 font-bold">Speed-based turns.</span>
                {" "}<span className="text-emerald-300 font-bold">Random loot.</span>
                {" "}<span className="text-amber-300 font-bold">Chits for everyone.</span>
              </p>
            </div>
          </div>
        </div>

        {/* Active-match banner — if user has a match running, surface Resume + Forfeit */}
        {activeMatchId && (
          <div
            data-testid="active-match-banner"
            className="rounded-2xl border-2 border-amber-500/70 bg-gradient-to-r from-amber-950/60 via-red-950/40 to-amber-950/60 p-4 mb-4 flex items-center justify-between flex-wrap gap-3"
          >
            <div className="flex items-center gap-3">
              <div className="text-3xl">⚔️</div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Match in progress</div>
                <div className="text-sm text-amber-100">You&rsquo;re still in a fight. Resume or forfeit to free yourself.</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/coliseum/match/${activeMatchId}`}
                data-testid="resume-match-btn"
                className="px-4 py-2 rounded-lg bg-amber-700 hover:bg-amber-600 text-white text-sm font-black"
              >
                ▶ Resume Match
              </Link>
              <button
                type="button"
                data-testid="forfeit-from-lobby-btn"
                onClick={async () => {
                  if (!window.confirm("Forfeit this match? Opposing side wins (no rewards).")) return;
                  setBusy(true);
                  const r = await postAction(`/coliseum/match/${activeMatchId}/forfeit`);
                  setBusy(false);
                  if (r.ok) {
                    toast.success("Match forfeited");
                    setActiveMatchId(null);
                    bump();
                  } else {
                    toast.error(r.error || "Forfeit failed.");
                  }
                }}
                disabled={busy}
                className="px-4 py-2 rounded-lg border border-red-700/60 bg-red-900/30 hover:bg-red-800/40 text-red-200 text-sm font-bold disabled:opacity-50"
              >
                🏳️ Forfeit
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-red-800/60 bg-red-950/40 p-4 mb-6 text-red-200 text-sm" data-testid="coliseum-error">
            {error}
          </div>
        )}

        {/* Trophy case (W/L + last 5 matches) */}
        <TrophyCase history={history} />

        {/* Pending invites */}
        {invites.length > 0 && (
          <div className="rounded-2xl border-2 border-amber-700/60 bg-gradient-to-br from-amber-950/40 to-black/60 p-5 mb-6" data-testid="coliseum-invites">
            <div className="text-[10px] uppercase tracking-widest text-amber-300/70 mb-2">Pending Invites · {invites.length}</div>
            <div className="space-y-3">
              {invites.map((inv) => (
                <div key={inv.party_id}
                     className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-black/50 border border-amber-800/40"
                     data-testid={`invite-${inv.party_id}`}>
                  <div className="text-2xl">{inv.mode === "dungeon_3" ? "🏰" : "⚔️"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-amber-100">{inv.leader_name} {inv.leader_handle && <span className="text-amber-400/70 font-normal">@{inv.leader_handle}</span>}</div>
                    <div className="text-xs text-neutral-400">
                      {inv.mode === "dungeon_3" ? "Party Dungeon" : "4v4 PvP"} · {inv.members_count}/{inv.max_party_size || 4} in party
                    </div>
                  </div>
                  <button onClick={() => onAccept(inv.party_id)} disabled={busy}
                          data-testid={`accept-invite-${inv.party_id}`}
                          className="px-4 py-1.5 rounded bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold text-xs">
                    Accept
                  </button>
                  <button onClick={() => onDecline(inv.party_id)} disabled={busy}
                          data-testid={`decline-invite-${inv.party_id}`}
                          className="px-4 py-1.5 rounded bg-neutral-800 hover:bg-neutral-700 disabled:opacity-50 text-neutral-300 font-bold text-xs">
                    Decline
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mode selector — always visible */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {(state?.modes || []).map((m) => {
            const active = partyMode === m.id;
            const t = MODE_THEME[m.id] || MODE_THEME.pvp_3v3;
            return (
              <button
                key={m.id}
                onClick={() => onSwitchMode(m.id)}
                disabled={busy || isQueued}
                data-testid={`mode-${m.id}`}
                className={`text-left rounded-2xl border-2 p-4 transition-all ${active ? `${t.border} bg-gradient-to-br ${t.bg} ring-2 ring-amber-500/30` : "border-neutral-800 bg-black/40 hover:border-neutral-700"} disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                <div className="flex items-center gap-3">
                  <div className="text-3xl">{m.icon}</div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest text-neutral-400">{m.party_size}-Player</div>
                    <div className={`text-xl font-black ${active ? t.accent : "text-neutral-100"}`} style={{ fontFamily: "Anton, sans-serif" }}>
                      {m.label}
                    </div>
                  </div>
                  {active && <span className="text-amber-300 text-xs font-bold">SELECTED</span>}
                </div>
                <p className="text-xs text-neutral-400 mt-2">{m.desc}</p>
              </button>
            );
          })}
        </div>

        {/* Party panel */}
        {!party ? (
          <div className="rounded-2xl border-2 border-dashed border-neutral-800 bg-black/30 p-8 text-center" data-testid="no-party-panel">
            <div className="text-5xl mb-3">🧭</div>
            <div className="text-lg font-black text-neutral-200" style={{ fontFamily: "Anton, sans-serif" }}>
              YOU&rsquo;RE NOT IN A PARTY YET
            </div>
            <p className="text-sm text-neutral-400 mt-2 max-w-md mx-auto">
              Open a party for <b className={theme.accent}>{partyMode === "dungeon_3" ? "Party Dungeon" : "4v4 PvP"}</b> and invite up to three more heroes by their handle.
            </p>
            <button
              onClick={onCreateParty}
              disabled={busy}
              data-testid="create-party-btn"
              className="mt-5 px-6 py-2.5 rounded-full bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white font-black tracking-wide"
            >
              + OPEN PARTY
            </button>
          </div>
        ) : (
          <div className={`rounded-2xl border-2 ${theme.border} bg-gradient-to-br ${theme.bg} p-5`} data-testid="party-panel">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300/70">Your Party</div>
                <div className="text-2xl font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>
                  {party.mode === "dungeon_3" ? "🏰 PARTY DUNGEON" : "⚔️ 4v4 PVP"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs border ${theme.badge} font-bold`} data-testid="team-power">
                  ⚡ Power {teamPower.toLocaleString()}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs border ${theme.badge} font-bold`}>
                  {party.members.length}/{maxParty} heroes
                </span>
                {isLeader && <span className="px-3 py-1 rounded-full text-xs border border-amber-700/60 bg-amber-900/40 text-amber-200 font-bold">👑 Leader</span>}
              </div>
            </div>

            {/* 🎯 Column-based formation grid (4 cols × 2 rows) — Feb 2026 owner spec.
                • Max 4 heroes. One hero per column. Each hero has a Front/Back toggle.
                • The OTHER seat in the same column is reserved for future Units.
                • Never render 8 usable hero slots — columns are the source of truth.
                • Combat: front row is targeted first, back row last (respected by combat engine). */}
            <div className="mb-4" data-testid="party-formation-grid">
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="text-xs uppercase tracking-widest text-neutral-300 font-bold">
                  ⚔️ Battle Formation · <span className="text-amber-300">4 columns</span>
                </div>
                <span className="text-[10px] text-neutral-500">
                  Front row hit first · Back row hit last · Empty seats reserved for Units
                </span>
              </div>
              {/* 🔁 Roc #52 — tap-to-swap hint banner. Shows only during setup. */}
              {isLeader && !isQueued && (
                <div className="mb-2 px-2 py-1.5 rounded-md border border-sky-700/40 bg-sky-950/30 text-[11px] text-sky-200 flex items-center justify-between gap-2"
                     data-testid="formation-swap-hint">
                  {selectedUserId ? (
                    <>
                      <span>🔁 <b>Tap another hero</b> to swap their position with the selected one · or tap the same hero to cancel.</span>
                      <button type="button"
                              onClick={() => setSelectedUserId(null)}
                              data-testid="formation-swap-cancel"
                              className="text-[10px] uppercase tracking-wider font-bold text-sky-300 hover:text-sky-100">
                        Cancel
                      </button>
                    </>
                  ) : (
                    <span className="text-sky-300/80">💡 Tip: Tap any hero to <b>select</b>, then tap another to swap their seats. Positions lock when the battle starts.</span>
                  )}
                </div>
              )}
              <div className="grid grid-cols-4 gap-2 md:gap-3" data-testid="formation-columns">
                {Array.from({ length: maxParty }).map((_, colIdx) => {
                  const m = party.members[colIdx] || null;
                  const columnNum = colIdx + 1;
                  if (!m) {
                    // Empty column — no hero here yet. Both seats blank.
                    return (
                      <div key={`col-${columnNum}`}
                           data-testid={`formation-col-${columnNum}-empty`}
                           className="rounded-xl border border-dashed border-neutral-800 bg-black/20 p-2 flex flex-col gap-2">
                        <div className="text-[9px] uppercase tracking-widest text-neutral-600 text-center">Col {columnNum}</div>
                        <div className="rounded-md border border-neutral-900 bg-black/30 min-h-[60px] flex items-center justify-center text-[10px] text-neutral-700">Front · empty</div>
                        <div className="rounded-md border border-neutral-900 bg-black/30 min-h-[60px] flex items-center justify-center text-[10px] text-neutral-700">Back · empty</div>
                      </div>
                    );
                  }
                  const heroRow = (m.row || "front");
                  const otherRow = heroRow === "front" ? "back" : "front";
                  const isLeaderSlot = m.user_id === party.leader_user_id;
                  const isMe = me && m.user_id === me.user_id;
                  const isSelected = selectedUserId === m.user_id;
                  const canTapToSwap = isLeader && !isQueued;
                  const HeroCard = (
                    <div className={`rounded-md border p-2 min-h-[112px] flex flex-col transition ${
                           isSelected
                             ? "border-sky-400 bg-gradient-to-b from-sky-900/60 to-black/70 ring-2 ring-sky-400/60"
                             : "border-amber-700/40 bg-gradient-to-b from-amber-950/30 to-black/60"
                         } ${canTapToSwap ? "cursor-pointer hover:border-amber-500/70" : ""}`}
                         onClick={canTapToSwap ? () => onTapHero(m.user_id) : undefined}
                         data-testid={`party-member-${m.user_id}`}
                         title={canTapToSwap
                           ? (selectedUserId && selectedUserId !== m.user_id
                               ? `Tap to swap positions with the selected hero`
                               : `Tap to select, then tap another hero to swap their seats`)
                           : undefined}>
                      <div className="flex items-center gap-1.5">
                        <div className="text-xl">{m.pet_icon}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-bold text-neutral-100 truncate flex items-center gap-1">
                            {m.name}
                            {isLeaderSlot && <span title="Leader">👑</span>}
                            {isMe && <span className="text-amber-400/70 text-[9px]">(you)</span>}
                          </div>
                          <div className="text-[9px] text-neutral-500 truncate">{m.handle ? `@${m.handle}` : ""}</div>
                        </div>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1 text-[9px]">
                        <span className="px-1 py-0.5 rounded-full bg-neutral-900 border border-neutral-800 text-neutral-300">
                          {CLASS_LABEL[m.hero_class] || m.hero_class}
                        </span>
                        <span className="px-1 py-0.5 rounded-full bg-amber-950/40 border border-amber-800/40 text-amber-200">
                          ⚡ {m.power_level}
                        </span>
                        {!m.is_alive && <span className="px-1 py-0.5 rounded-full bg-red-950/40 border border-red-800/40 text-red-300">💀</span>}
                      </div>
                      <div className="mt-auto pt-1 flex items-center gap-1 flex-wrap">
                        {isLeader && !isQueued && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onMoveRow(m.user_id, otherRow); }}
                            disabled={busy}
                            data-testid={`move-row-${m.user_id}`}
                            className="text-[10px] px-2 py-1 rounded border-2 border-sky-500 bg-sky-900/60 text-sky-100 hover:bg-sky-700 hover:border-sky-300 disabled:opacity-40 font-bold uppercase tracking-wider shadow-sm"
                            title={`Move ${m.name} to ${otherRow} row`}
                          >
                            {otherRow === "front" ? "↑ Move to Front" : "↓ Move to Back"}
                          </button>
                        )}
                        {isLeader && !isMe && !isQueued && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onKick(m.user_id, m.name); }}
                            disabled={busy}
                            data-testid={`kick-${m.user_id}`}
                            className="text-[9px] text-red-400 hover:text-red-300 uppercase tracking-wider font-bold"
                          >Kick</button>
                        )}
                      </div>
                    </div>
                  );
                  const ReservedSeat = (
                    <div
                      className="rounded-md border border-dashed border-neutral-800/70 bg-black/30 min-h-[60px] flex items-center justify-center text-[9px] text-neutral-600 px-1 text-center leading-tight"
                      data-testid={`formation-col-${columnNum}-reserved-${otherRow}`}
                    >
                      🛡️ Reserved<br/>for Unit
                    </div>
                  );
                  return (
                    <div key={`col-${columnNum}`}
                         data-testid={`formation-col-${columnNum}`}
                         className="rounded-xl border border-neutral-800 bg-black/30 p-2 flex flex-col gap-2">
                      <div className="text-[9px] uppercase tracking-widest text-amber-400/60 text-center">
                        Col {columnNum} · {heroRow === "front" ? "🛡️ Front" : "🏹 Back"}
                      </div>
                      {heroRow === "front" ? HeroCard : ReservedSeat}
                      {heroRow === "front" ? ReservedSeat : HeroCard}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pending invites in party */}
            {(party.invites?.length || 0) > 0 && (
              <div className="rounded-xl border border-neutral-800 bg-black/40 p-3 mb-4">
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">Outgoing Invites</div>
                <div className="space-y-2">
                  {party.invites.map((inv) => (
                    <div key={inv.user_id} className="flex items-center gap-3 text-xs" data-testid={`outgoing-${inv.user_id}`}>
                      <span className="text-neutral-300">✉️ {inv.name}</span>
                      {inv.handle && <span className="text-neutral-500">@{inv.handle}</span>}
                      <span className="text-neutral-600">· {elapsed(inv.invited_at) || "0s"} ago</span>
                      {isLeader && !isQueued && (
                        <button onClick={() => onCancelInvite(inv.user_id)} disabled={busy}
                                data-testid={`cancel-${inv.user_id}`}
                                className="ml-auto text-red-400 hover:text-red-300 uppercase font-bold tracking-wider">
                          Cancel
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-match Class Picker — pick which hero to channel for the
                NEXT match. Locked classes work too (Coliseum/Arcade sneak peek 😈) */}
            {me && !isQueued && (
              <div className="rounded-xl border border-amber-900/40 bg-amber-950/20 p-3 mb-4" data-testid="match-class-picker">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-300/70">⚔️ Your Match Class</div>
                    <div className="text-xs text-amber-100/80">
                      Channel a hero for THIS match — 🔒 locked ones work too (sneak-peek demo)
                    </div>
                  </div>
                  {party.match_class_overrides?.[me.user_id] && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] border border-amber-600 bg-amber-700/40 text-amber-100 font-bold">
                      {(ALL_MATCH_CLASSES.find((c) => c.id === party.match_class_overrides[me.user_id])?.name) ||
                       party.match_class_overrides[me.user_id].replace(/_/g, " ").toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {ALL_MATCH_CLASSES.map((c) => {
                    const active = (party.match_class_overrides?.[me.user_id] || me.hero_class) === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => onPickMatchClass(c.id)}
                        disabled={busy}
                        data-testid={`match-class-${c.id}`}
                        title={c.tagline}
                        className={`text-left p-2.5 rounded-lg border transition-all disabled:opacity-50 ${active
                          ? "bg-amber-700/40 border-amber-300 text-amber-50 ring-2 ring-amber-400/30"
                          : c.locked
                            ? "bg-neutral-900 border-neutral-800 text-neutral-300 hover:border-amber-700"
                            : "bg-neutral-900 border-neutral-700 text-neutral-200 hover:border-amber-700"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {/* 🖼️ Owner-supplied portrait when available; emoji fallback otherwise.
                              `object-cover` on a tight aspect keeps the face/silhouette readable
                              at card scale. */}
                          {c.portrait ? (
                            <img
                              src={c.portrait}
                              alt={c.name}
                              data-testid={`match-class-${c.id}-portrait`}
                              className="w-12 h-12 rounded-md object-cover bg-black/40 border border-amber-900/40 flex-shrink-0"
                              loading="lazy"
                            />
                          ) : (
                            <div
                              className="w-12 h-12 rounded-md flex items-center justify-center text-3xl flex-shrink-0 relative overflow-hidden"
                              data-testid={`match-class-${c.id}-portrait-fallback`}
                              style={{
                                background: `radial-gradient(circle at 30% 20%, ${c.accent || "#D4AF37"}55, #000 75%)`,
                                border: `1px solid ${c.accent || "#D4AF37"}88`,
                                boxShadow: `inset 0 0 12px ${c.accent || "#D4AF37"}22, 0 0 6px ${c.accent || "#D4AF37"}33`,
                              }}
                            >
                              <span className="drop-shadow-[0_0_4px_rgba(0,0,0,0.9)]">{c.icon}</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <span className="font-bold text-[12px] leading-tight flex-1 break-words" title={c.name}>{c.name}</span>
                              {c.locked && (
                                <span
                                  className="text-[9px] px-1.5 py-0.5 rounded-full border border-amber-900/60 bg-amber-950/40 text-amber-300/70"
                                  data-testid={`match-class-${c.id}-locked-chip`}
                                  title="Channeled — locked in the world, allowed here as a sneak peek"
                                >
                                  🔒
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-neutral-400 mt-0.5 leading-snug line-clamp-2">
                              {c.tagline}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {(c.skills || []).map((s, i) => (
                                <span
                                  key={i}
                                  className="text-[9px] px-1.5 py-0.5 rounded bg-black/40 border border-neutral-800 text-amber-200/80"
                                  data-testid={`match-class-${c.id}-skill-${i}`}
                                >
                                  {s}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Invite by handle */}
            {isLeader && party.members.length < maxParty && !isQueued && (
              <div className="rounded-xl border border-neutral-800 bg-black/40 p-3 mb-4">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400">Invite a Hero</div>
                  <button
                    onClick={() => setShowPicker((v) => !v)}
                    data-testid="toggle-picker-btn"
                    className="text-[10px] uppercase tracking-widest text-amber-300 hover:text-amber-200 font-bold"
                  >
                    {showPicker ? "▴ Hide picker" : "▾ Pick from friends / guildmates"}
                  </button>
                </div>

                {showPicker && (
                  <div className="mb-3 rounded-lg border border-neutral-800 bg-black/30 p-2" data-testid="invite-picker">
                    <div className="flex gap-1 mb-2">
                      <button
                        onClick={() => setPickerTab("guildmates")}
                        data-testid="picker-tab-guildmates"
                        className={`flex-1 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider ${pickerTab === "guildmates" ? "bg-indigo-700 text-white" : "bg-neutral-900 text-neutral-400 hover:text-neutral-200"}`}
                      >
                        🐦 Guildmates ({candidates?.guildmates?.length || 0})
                      </button>
                      <button
                        onClick={() => setPickerTab("friends")}
                        data-testid="picker-tab-friends"
                        className={`flex-1 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider ${pickerTab === "friends" ? "bg-pink-700 text-white" : "bg-neutral-900 text-neutral-400 hover:text-neutral-200"}`}
                      >
                        🤝 Friends ({candidates?.friends?.length || 0})
                      </button>
                    </div>
                    <CandidateList
                      list={pickerTab === "friends" ? candidates?.friends : candidates?.guildmates}
                      emptyMsg={
                        pickerTab === "friends"
                          ? "No friends yet — add some from a player's public profile."
                          : (candidates?.guild_name
                              ? `No guildmates in ${candidates.guild_name} available to invite.`
                              : "Join a guild to see guildmates here.")
                      }
                      onInvite={onInviteById}
                      busy={busy}
                    />
                  </div>
                )}

                <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">Or invite by handle</div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="@phoenix_handle"
                    value={inviteHandle}
                    onChange={(e) => setInviteHandle(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && onInvite()}
                    data-testid="invite-handle-input"
                    className="flex-1 px-3 py-2 rounded bg-neutral-900 border border-neutral-800 focus:border-amber-700 outline-none text-sm"
                  />
                  <button
                    onClick={onInvite}
                    disabled={busy || !inviteHandle.trim()}
                    data-testid="send-invite-btn"
                    className="px-4 py-2 rounded bg-amber-700 hover:bg-amber-600 disabled:opacity-50 text-white font-bold text-xs"
                  >
                    Invite
                  </button>
                </div>
              </div>
            )}

            {/* Same-gear matchmaking toggle (PvP only, leader-only, not queued) */}
            {party.mode === "pvp_3v3" && isLeader && !isQueued && (
              <div className="mt-3 flex items-start gap-3 rounded-xl border border-amber-900/40 bg-black/40 p-3" data-testid="same-gear-card">
                <input
                  id="same-gear-toggle"
                  data-testid="same-gear-toggle"
                  type="checkbox"
                  checked={!!party.same_gear_matching}
                  disabled={busy}
                  onChange={async (e) => {
                    setBusy(true);
                    const r = await postAction("/coliseum/party/same-gear", { enabled: e.target.checked });
                    setBusy(false);
                    if (r.ok) {
                      toast.success(e.target.checked ? "Same-gear matchmaking ON" : "Same-gear matchmaking OFF");
                      bump();
                    }
                  }}
                  className="mt-0.5 w-4 h-4 accent-amber-600"
                />
                <label htmlFor="same-gear-toggle" className="cursor-pointer flex-1">
                  <div className="text-xs font-bold text-amber-200">🛡️ Same-Gear Matchmaking</div>
                  <div className="text-[10px] text-neutral-400 mt-0.5">
                    Only pair with parties whose total gear power is within <span className="text-amber-300 font-bold">±15%</span> of yours.
                    Keeps fights fair — may lengthen queue.
                  </div>
                </label>
              </div>
            )}

            {/* Queue controls */}
            <div className="flex flex-wrap gap-2 mt-3">
              {!isQueued ? (
                <div className="flex-1 min-w-[200px]">
                  <button
                    onClick={onQueueEnter}
                    disabled={busy || !isLeader || party.members.length < 1}
                    data-testid="queue-enter-btn"
                    className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-red-700 to-rose-700 hover:from-red-600 hover:to-rose-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black tracking-wider shadow-lg"
                  >
                    {party.members.length < maxParty
                      ? `🎯 ENTER MATCHMAKING — 🤖 ${maxParty - party.members.length} AI ${(maxParty - party.members.length) === 1 ? "ally" : "allies"} will fill in`
                      : "🎯 ENTER MATCHMAKING"}
                  </button>
                  {/* 🩹 Feb 2026 owner spec (B7): tell the player WHY the
                      queue button is disabled instead of leaving them
                      to guess. */}
                  {(!isLeader || party.members.length < 1 || busy) && (
                    <div
                      className="text-[10px] text-neutral-500 mt-1 italic text-center"
                      data-testid="queue-disabled-reason"
                    >
                      {!isLeader
                        ? "Only the party leader can start matchmaking."
                        : party.members.length < 1
                          ? "Add at least one hero before queuing."
                          : busy
                            ? "Working…"
                            : ""}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={onQueueLeave}
                  disabled={busy || !isLeader}
                  data-testid="queue-leave-btn"
                  className="flex-1 min-w-[200px] px-6 py-3 rounded-xl bg-neutral-800 hover:bg-neutral-700 disabled:opacity-40 text-amber-200 font-black tracking-wider"
                >
                  🛑 LEAVE QUEUE — searching {elapsed(party.queued_at) || "0s"} · {clock % 2 === 0 ? "•" : "·"}
                </button>
              )}
              <button
                onClick={onLeave}
                disabled={busy}
                data-testid="leave-party-btn"
                className="px-5 py-3 rounded-xl bg-black/60 hover:bg-black/80 border border-neutral-800 text-neutral-300 font-bold text-xs"
              >
                Leave Party
              </button>
            </div>

            {isQueued && (
              <div className="mt-3 text-xs italic" data-testid="queue-status-line">
                {party.mode === "pvp_3v3" ? (
                  state?.ai_backup_in_seconds !== undefined && state?.ai_backup_in_seconds !== null ? (
                    state.ai_backup_in_seconds > 0 ? (
                      <span className="text-amber-200/90" data-testid="ai-backup-countdown">
                        🔎 Scanning for human guilds… 🤖 AI rival squad spins up in <span className="font-black text-amber-100">{state.ai_backup_in_seconds}s</span> if no one shows.
                      </span>
                    ) : (
                      <span className="text-amber-100 animate-pulse" data-testid="ai-backup-spinning">
                        🤖 Spinning up the AI rival squad now…
                      </span>
                    )
                  ) : (
                    <span className="text-amber-200/80">🔎 Hunting opponents…</span>
                  )
                ) : (
                  <span className="text-amber-200/80">
                    🏰 Dungeon doors prepping — the run auto-starts in a moment.
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer help */}
        <div className="mt-6 rounded-xl border border-neutral-900 bg-black/40 p-4 text-xs text-neutral-400">
          <div className="font-bold text-neutral-300 mb-1">⚔️ How Coliseum works (Phase 1)</div>
          <ul className="list-disc pl-5 space-y-1">
            <li>Each party fits up to 4 heroes — invite by player <b>handle</b>.</li>
            <li>Power = your pet&rsquo;s HP + ATK·4 + DEF·3 + SPD·2 + CRIT. Higher tier = more power.</li>
            <li>3v3 PvP uses strict turn-based, speed-fastest-first order across all 6 heroes.</li>
            <li>Party Dungeon = co-op crawl through 3 rooms (boss at room 3).</li>
            <li>Idle players auto-attack after 7 seconds. Random loot + shared Chits payout.</li>
          </ul>
        </div>
      </div>
      <Footer />
    </div>
  );
}
